import { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import WorldMap from '../components/WorldMap';
import GeographicDataList from '../components/GeographicDataList';
import DonutChart from '../components/DonutChart';
import TopPagesAndStatus from '../components/TopPagesAndStatus';
import { Icon } from '../utils/icons';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

const Analytics = () => {
    usePageTitle('Analytics');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [timeframe, setTimeframe] = useState('30d');
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [widgets, setWidgets] = useState([]);

    // Fetch dashboard analytics
    const fetchDashboardAnalytics = async (selectedTimeframe = timeframe) => {
        try {
            setLoading(true);
            const response = await api.get(`/analytics/dashboard?timeframe=${selectedTimeframe}`);
            setAnalyticsData(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch analytics data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch widgets for the dropdown
    const fetchWidgets = async () => {
        try {
            const response = await api.get('/widgets');
            setWidgets(response.data.widgets || []);
        } catch (err) {
            // Silent error handling for widget fetch
        }
    };

    // Fetch analytics for specific widget
    const fetchWidgetAnalytics = async (widgetId, selectedTimeframe = timeframe) => {
        try {
            setLoading(true);
            const response = await api.get(`/analytics/widget/${widgetId}?timeframe=${selectedTimeframe}`);
            setAnalyticsData(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch widget analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWidgets();
        if (selectedWidget) {
            fetchWidgetAnalytics(selectedWidget, timeframe);
        } else {
            fetchDashboardAnalytics(timeframe);
        }
    }, [timeframe, selectedWidget]);

    const handleTimeframeChange = (newTimeframe) => {
        setTimeframe(newTimeframe);
    };

    const handleWidgetChange = (widgetId) => {
        setSelectedWidget(widgetId);
    };

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num?.toString() || '0';
    };

    const formatLoadTime = (time) => {
        if (!time) return 'N/A';
        return time < 1000 ? `${Math.round(time)}ms` : `${(time / 1000).toFixed(2)}s`;
    };


    // Skeleton Loading Components
    const MetricCardSkeleton = () => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="flex items-center">
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
            </div>
        </div>
    );

    const ChartSkeleton = ({ title: _title }) => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-5 h-5 bg-gray-300 rounded"></div>
                            <div className="h-4 bg-gray-300 rounded w-20"></div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-20 bg-gray-200 rounded-full h-2"></div>
                            <div className="h-4 bg-gray-300 rounded w-12"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const TableSkeleton = ({ title: _title, rows = 5 }) => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="overflow-hidden">
                <div className="space-y-3">
                    {[...Array(rows)].map((_, i) => (
                        <div key={i} className="flex items-center justify-between py-2">
                            <div className="flex-1">
                                <div className="h-4 bg-gray-300 rounded w-32 mb-1"></div>
                                <div className="h-3 bg-gray-200 rounded w-24"></div>
                            </div>
                            <div className="h-4 bg-gray-300 rounded w-12"></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    {/* Header Skeleton */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-64"></div>
                        </div>
                        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3 animate-pulse">
                            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
                            <div className="h-10 bg-gray-200 rounded-lg w-48"></div>
                        </div>
                    </div>

                    {/* Summary Cards Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <MetricCardSkeleton key={i} />
                        ))}
                    </div>

                    {/* Charts Row Skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ChartSkeleton title="Device Breakdown" />
                        <ChartSkeleton title="Geographic Distribution" />
                    </div>

                    {/* Tables Row Skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TableSkeleton title="Top Pages" />
                        <TableSkeleton title="Analytics Status" />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Track your widget performance and user engagement
                        </p>
                    </div>

                    <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
                        {/* Widget Selector */}
                        <div className="relative">
                            <select
                                value={selectedWidget || ''}
                                onChange={(e) => handleWidgetChange(e.target.value || null)}
                                className="appearance-none px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                                style={{ backgroundImage: 'none' }}
                            >
                                <option value="">All Widgets</option>
                                {widgets.map(widget => (
                                    <option key={widget._id} value={widget._id}>
                                        {widget.name}
                                    </option>
                                ))}
                            </select>
                            {/* Dropdown arrow */}
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <Icon name="dropdown" size={20} className="text-gray-400" />
                            </div>
                        </div>

                        {/* Timeframe Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {[
                                { value: '7d', label: '7D' },
                                { value: '30d', label: '30D' },
                                { value: '90d', label: '90D' },
                                { value: '1y', label: '1Y' }
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    onClick={() => handleTimeframeChange(value)}
                                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${timeframe === value
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex">
                            <Icon name="warning" size={20} className="text-red-400" />
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {analyticsData && (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <Icon name="visibility" size={20} className="text-blue-600" />
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Views</dt>
                                            <dd className="text-lg font-semibold text-gray-900">{formatNumber(analyticsData.summary?.totalViews)}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                            <Icon name="people" size={20} className="text-green-600" />
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Unique Visitors</dt>
                                            <dd className="text-lg font-semibold text-gray-900">{formatNumber(analyticsData.summary?.uniqueVisitors)}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                                            <Icon name="time" size={20} className="text-yellow-600" />
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Avg Load Time</dt>
                                            <dd className="text-lg font-semibold text-gray-900">{formatLoadTime(analyticsData.summary?.averageLoadTime)}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                            <Icon name="widgets" size={20} className="text-purple-600" />
                                        </div>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Widgets</dt>
                                            <dd className="text-lg font-semibold text-gray-900">{analyticsData.summary?.totalWidgets || widgets.length || 0}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Device Breakdown Donut Chart */}
                            <DonutChart
                                data={analyticsData.deviceBreakdown}
                                title="Device Breakdown"
                            />

                            {/* World Map Visualization */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-hidden">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Global Traffic Overview</h3>
                                        <p className="text-sm text-gray-600 mt-1">Interactive world map showing traffic distribution by country</p>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Click and drag to explore • Use controls to zoom
                                    </div>
                                </div>
                                <div className="overflow-hidden rounded-lg">
                                    <WorldMap
                                        geographicData={analyticsData.geographicData}
                                        onCountryClick={(_countryData) => {
                                            // Future: Could show detailed breakdown for country
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Performance Overview and Geographic Traffic Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <TopPagesAndStatus
                                analyticsData={analyticsData}
                                selectedWidget={selectedWidget}
                                widgets={widgets}
                                formatNumber={formatNumber}
                            />
                            <GeographicDataList
                                geographicData={analyticsData.geographicData}
                                isLoading={loading}
                            />
                        </div>
                    </>
                )}

                {/* No Data State */}
                {!analyticsData && !loading && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-8 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="analytics" size={32} className="text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-blue-900 mb-2">No Analytics Data Yet</h3>
                            <p className="text-blue-700 mb-4">
                                Start by embedding your widgets on websites to collect analytics data.
                            </p>
                            <div className="text-sm text-blue-600">
                                <p>Analytics tracking is active and ready to collect data from your embedded widgets.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Analytics; 